import React, { useState } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { useDropzone } from "react-dropzone";
import { Toast, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv, faFileExcel } from "@fortawesome/free-solid-svg-icons";

// Mapping for headers
const headerMap = {
  "Community Name": "communityName",
  "Community Email": "communityEmail",
  "Community Phone Number": "communityPhoneNumber",
  "Address": "address",
  "Region": "region",
  "First Name": "firstName",
  "Last Name": "lastName",
  "Work Email": "workEmail",
  "Phone Number": "phoneNumber"
};

const normalizeHeaders = (row) => {
  const normalized = {};
  Object.entries(row).forEach(([key, value]) => {
    const camelKey = headerMap[key.trim()];
    if (camelKey) {
      normalized[camelKey] = value;
    }
  });
  return normalized;
};

function FileUpload({ onDataParsed }) {
  const [isUploading, setIsUploading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const handleFile = (file) => {
    const reader = new FileReader();
    setIsUploading(true);

    try {
      if (file.name.endsWith(".csv")) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            onDataParsed(results.data.map(normalizeHeaders));
            setIsUploading(false);
          },
          error: () => {
            showToastMessage("Error parsing CSV file", false);
            setIsUploading(false);
          },
        });
      } else {
        reader.onload = (evt) => {
          const bstr = evt.target.result;
          const workbook = XLSX.read(bstr, { type: "binary" });
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          onDataParsed(data.map(normalizeHeaders));
          setIsUploading(false);
        };
        reader.readAsBinaryString(file);
      }
    } catch (error) {
      showToastMessage("File parsing failed", false);
      setIsUploading(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      handleFile(acceptedFiles[0]);
    }
  };

  const showToastMessage = (message, isSuccess) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  return (
    <div className="px-0">
      {/* Toast */}
      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={3000}
        autohide
        bg={toastMessage.includes("successfully") ? "success" : "danger"}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1050 }}
      >
        <Toast.Header>
          <strong className="me-auto">
            {toastMessage.includes("successfully") ? "Success" : "Error"}
          </strong>
        </Toast.Header>
        <Toast.Body className={toastMessage.includes("successfully") ? "text-white" : ""}>
          {toastMessage}
        </Toast.Body>
      </Toast>

      {/* Uploading Spinner */}
      {isUploading && (
        <div className="fullscreen-loader text-center">
          <Spinner animation="border" />
          <div className="mt-2">Parsing file...</div>
        </div>
      )}

      {/* Dropzone UI */}
      <div
        {...getRootProps()}
        className={`dropzone-wrapper border border-2 rounded p-4 text-center ${isDragActive ? "border-primary" : "border-secondary"}`}
        style={{
          background: isDragActive ? "#f0f8ff" : "#fafafa",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
      >
        <input {...getInputProps()} />
        <div className="d-flex flex-column align-items-center">
          <div className="d-flex justify-content-center align-items-center">
            <FontAwesomeIcon icon={faFileCsv} size="2x" style={{ color: "green" }} />
            <FontAwesomeIcon icon={faFileExcel} size="2x" style={{ color: "blue", marginTop: "5px" }} className="mx-3" />
          </div>
          <p className="mt-2 mb-1 fw-bold">
            Drag and drop a .csv or Excel file, or click to select
          </p>
          <small className="text-muted">Accepted formats: .csv, .xlsx, .xls</small>
        </div>
      </div>
    </div>
  );
}

export default FileUpload;
